import { useEffect } from "react";
import { io, type Socket } from "socket.io-client";
import { usePhysicianAuthStore } from "@/stores/physicianAuthStore";
import { mocksEnabled } from "@/portals/physician/api/transport";
import { hardenSocket } from "@/lib/socketRecovery";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// Separate from socketClient.ts's patient socket on purpose: that one is bound
// to the patient store (and currently switched off). `forceNew` keeps this on
// its own Manager/connection, so the two can never share a handshake token.
// The server joins it to user:{userId} and physicians. Module 4 listens for
// review_queue:item_added / item_removed (see usePhysicianReviewLive) and
// notification:new (see physicianNotificationsStore).
let socket: Socket | null = null;

export function getPhysicianSocket(): Socket {
  if (socket) return socket;
  socket = io(BASE_URL || undefined, {
    autoConnect: false,
    forceNew: true,
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  socket.on("connect", () => console.info("[realtime:physician] connected", socket?.id));
  socket.on("disconnect", (reason) => console.warn("[realtime:physician] disconnected:", reason));
  socket.on("connect_error", (err) => console.error("[realtime:physician] failed to connect:", err.message));
  hardenSocket(socket, () => Boolean(usePhysicianAuthStore.getState().accessToken));
  return socket;
}

export function syncPhysicianSocket(token: string | null): void {
  if (mocksEnabled()) return; // no Socket.IO server behind the mocks
  const s = getPhysicianSocket();
  if (!token) {
    s.disconnect();
    return;
  }
  // Applies to the next (re)connect. The contract names no in-place re-auth
  // event for physicians, so a live connection keeps its handshake token.
  s.auth = { token };
  if (!s.connected) s.connect();
}

/** Mounted by the protected physician layout: connects after login, disconnects on logout/unmount. */
export function usePhysicianSocket(): void {
  const token = usePhysicianAuthStore((s) => s.accessToken);
  useEffect(() => {
    syncPhysicianSocket(token);
  }, [token]);
  useEffect(() => () => syncPhysicianSocket(null), []);
}
